import { useIntl } from 'react-intl';
import { createColumnHelper } from '@tanstack/react-table';
import { Role } from 'src/features/roles/utils/types';
import useFilters from 'src/registry/hooks/useFilters';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { useTitleStore } from 'src/stores/useTitleStore';
import { useListRoles } from 'src/features/roles/api/useListRoles';
import React from 'react';
import {
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import { rolesMessages } from 'src/features/roles/utils/messages';
import { commonMessages } from 'src/utils/messages';
import CreateRoleFormModal from 'src/features/roles/components/forms/CreateRoleFormModal';
import CellRoleActions from 'src/features/roles/components/tables/CellRoleActions';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import usePagination from 'src/registry/hooks/usePagination';

const sortingOptions = {
    initialSortingState: [{ id: 'name', desc: false }],
};
const searchOptions = { globalFilterKey: 'name' };

const columnHelper = createColumnHelper<Role>();

function RolesTable() {
    const { formatMessage } = useIntl();

    const permissionManageRoles = useHasPermissions([PERMISSION.MANAGE_ROLES]);

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch(searchOptions);
    const { querySortingOptions, ...sorting } = useSorting(sortingOptions);
    const { queryPaginationOptions, ...pagination } = usePagination();

    const payload = React.useMemo(
        () => ({
            ...queryFiltersOptions,
            ...querySearchOptions,
            ...querySortingOptions,
            ...queryPaginationOptions,
        }),
        [queryFiltersOptions, querySearchOptions, querySortingOptions, queryPaginationOptions],
    );

    const { data, isLoading } = useListRoles(payload);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(rolesMessages.roles));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            cell: (ctx) => <CellLinkWithIcon {...ctx} to={`/roles/${ctx.row.original.id}`} />,
            meta: {
                title: formatMessage(commonMessages.name),
            },
        }),
        // Always rendered - the cell gates edit/delete on its own so that read-only users still
        // get the dashboard shortcut.
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellRoleActions,
        }),
    ];

    return (
        <DataTable<Role, any>
            isLoading={isLoading}
            columns={columns}
            data={data}
            search={search}
            filters={filters}
            sorting={sorting}
            pagination={pagination}
            semantics={{
                title: formatMessage(rolesMessages.roles),
                entryName: formatMessage(rolesMessages.role).toLowerCase(),
                entryNames: formatMessage(rolesMessages.roles).toLowerCase(),
                placeholder: formatMessage(rolesMessages.role),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageRoles && { newEntry: CreateRoleFormModal }),
            }}
        />
    );
}

export default RolesTable;
